import { useAuthMe, useLogout } from '../hooks/useLocalStorage';
import { User, Menu, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

interface HeaderProps {
  onMenuToggle: () => void;
}

interface AuthUser {
  id: number;
  username: string;
  email: string;
  client_id: string;
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { data: user } = useAuthMe();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const logout = useLogout();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    logout.mutate()
  };

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm h-18 sticky top-0 z-30">
      <div className="px-4 md:px-6 py-2">
        <div className="flex items-center justify-between min-w-0">
          <div className="flex items-center space-x-2 md:space-x-3 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden p-1"
              onClick={onMenuToggle}
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="w-8 h-8 md:w-10 md:h-10 bg-workfinity-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs md:text-sm">W</span>
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="text-sm sm:text-base md:text-lg font-semibold text-black truncate">
                <span className="hidden sm:inline text-2xl">WorkFINITY® Configuration Management System</span>
                <span className="sm:hidden">Workflow Engine - Manage Workflows</span>
              </h1>
              <p className="text-md text-black-600 hidden md:block">WORKFLOW ENGINE - Manage Workflows</p>
            </div>
          </div>

          <div className="flex items-center space-x-2 md:space-x-4">

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setOpen(!open)}
                className="flex items-center space-x-2 hover:bg-black-100 px-2 py-1 rounded-lg transition"
              >
                <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm font-medium text-black hidden sm:block">
                  {user?.username ?? "User"}
                </span>
              </button>

              {open && (
                <div className="absolute right-0 mt-2 w-64 bg-white border rounded-lg shadow-lg z-50">
                  <div className="px-4 py-3 border-b">
                    <p className="mt-1 text-md">
                      <span className='font-semibold text-gray-900'>Client ID: </span> {user?.client_id}
                    </p>
                    <p className="mt-1 text-md">
                      <span className='font-semibold text-black'>Name: </span>{user?.username}
                    </p>
                  </div>

                  <div className="p-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-red-600 hover:bg-red-50"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
